/* eslint-disable import/no-cycle */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMoveSharp } from 'react-icons/io5';
import isEqual from 'lodash/isEqual';
import take from 'lodash/take';
import { Box, Flex, HStack, SystemStyleObject, Text, theme } from '@chakra-ui/core';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { DraggedItem, DropTarget, FormItemT } from './types';
import { DRAG_ITEM_TYPES } from './constants';
import { checkIfSectionDraggedInsideSection, isSectionSchema } from './utils';

interface DroppableZoneProps {
  path: number[];
  disabled?: boolean;
  parentId?: string | null;
  isAdvancedSectionTarget?: boolean;
  onDropItem: (dropTarget: DropTarget) => void;
  className?: string;
  sx?: SystemStyleObject;
  children?: React.ReactNode;
  hideDropZoneMessage?: boolean;
  withDivider?: boolean;
}

export const DroppableZone: React.FC<DroppableZoneProps> = React.memo(
  ({
    path: dropZonePath,
    disabled = false,
    parentId,
    isAdvancedSectionTarget = false,
    onDropItem,
    className = '',
    sx,
    children,
    hideDropZoneMessage = true,
    withDivider = false,
  }) => {
    const formSchema = useStoreState((state) => state.formBuilder.formSchema);
    const draggedItem = useStoreState((state) => state.formBuilder.draggedItem);
    const dropIndicator = useStoreState((state) => state.formBuilder.dropIndicator);
    const setDropIndicator = useStoreActions((actions) => actions.formBuilder.setDropIndicator);
    const endDrag = useStoreActions((actions) => actions.formBuilder.endDrag);

    const { t } = useTranslation('hrFormTemplate');

    const isDescendantOrSelf = (ancestorPath: number[], potentialDescendantPath: number[]): boolean => {
      if (isEqual(ancestorPath, potentialDescendantPath)) {
        return true;
      }
      if (
        potentialDescendantPath.length > ancestorPath.length &&
        isEqual(take(potentialDescendantPath, ancestorPath.length), ancestorPath)
      ) {
        return true;
      }
      return false;
    };

    const getDropValidation = React.useCallback(
      (
        currentDraggedItem: DraggedItem | null,
        currentDropZonePath: number[],
        currentDropZoneIsAdvancedTarget: boolean,
      ): { canDrop: boolean; shouldPropagate: boolean; dropEffect: 'copy' | 'move' | 'none' } => {
        let canDropThisZone = true;
        let shouldPropagateEvent = false;
        let effect: 'copy' | 'move' | 'none' = 'none';

        if (!currentDraggedItem) {
          return { canDrop: false, shouldPropagate: false, dropEffect: 'none' };
        }

        effect = currentDraggedItem.type === DRAG_ITEM_TYPES.PALETTE ? 'copy' : 'move';

        if (
          currentDraggedItem.type === DRAG_ITEM_TYPES.FORM_ITEM &&
          isSectionSchema(currentDraggedItem.itemData as FormItemT) &&
          currentDraggedItem.sourcePath
        ) {
          const draggedSectionPath = currentDraggedItem.sourcePath;
          const targetContainerPath = currentDropZonePath.slice(0, -1);

          if (isDescendantOrSelf(draggedSectionPath, targetContainerPath)) {
            canDropThisZone = false;
            effect = 'none';
            shouldPropagateEvent = false;
          }
        }
        const isSectionOnSectionDrop = checkIfSectionDraggedInsideSection({
          formSchema,
          draggedItem: currentDraggedItem,
          path: currentDropZonePath,
        });

        if (isSectionOnSectionDrop) {
          canDropThisZone = false;
          effect = 'none';
          shouldPropagateEvent = false;
        }

        if (canDropThisZone && currentDropZoneIsAdvancedTarget) {
          if (currentDraggedItem.type === DRAG_ITEM_TYPES.PALETTE) {
            canDropThisZone = false;
            effect = 'none';
            shouldPropagateEvent = true;
          } else if (currentDraggedItem.type === DRAG_ITEM_TYPES.FORM_ITEM && currentDraggedItem.sourcePath) {
            const draggedItemParentPath = currentDraggedItem.sourcePath.slice(0, -1);
            const targetContainerPath = currentDropZonePath.slice(0, -1);
            if (!isEqual(draggedItemParentPath, targetContainerPath)) {
              canDropThisZone = false;
              effect = 'none';
              shouldPropagateEvent = true;
            }
          }
        }
        return { canDrop: canDropThisZone, shouldPropagate: shouldPropagateEvent, dropEffect: effect };
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [isDescendantOrSelf],
    );

    const handleDragEnter = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault(); // Allow drop
        if (!draggedItem) return;

        const validation = getDropValidation(draggedItem, dropZonePath, isAdvancedSectionTarget);
        if (e.dataTransfer) /* eslint-disable-line */ e.dataTransfer.dropEffect = validation.dropEffect;

        if (validation.canDrop) {
          // Set indicator if this zone is now the target
          setDropIndicator({ path: dropZonePath, parentId, isAdvancedSectionTarget, show: true });
          e.stopPropagation(); // This zone is actively targeted
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [draggedItem, getDropValidation, dropZonePath, isAdvancedSectionTarget, parentId],
    );

    const handleDragOver = React.useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();

        if (!draggedItem) {
          if (e.dataTransfer) /* eslint-disable-line */ e.dataTransfer.dropEffect = 'none';
          e.stopPropagation();
          return;
        }

        const { canDrop, shouldPropagate, dropEffect } = getDropValidation(draggedItem, dropZonePath, isAdvancedSectionTarget);

        if (e.dataTransfer) {
          /* eslint-disable-line */ e.dataTransfer.dropEffect = dropEffect;
        }

        if (canDrop) {
          if (
            !dropIndicator ||
            !dropIndicator.show ||
            !isEqual(dropIndicator.path, dropZonePath) ||
            dropIndicator.parentId !== parentId
          ) {
            setDropIndicator({ path: dropZonePath, parentId, isAdvancedSectionTarget, show: true });
          }

          e.stopPropagation();
        } else {
          if (
            dropIndicator &&
            dropIndicator.show &&
            isEqual(dropIndicator.path, dropZonePath) &&
            dropIndicator.parentId === parentId
          ) {
            setDropIndicator(undefined);
          }
          if (!shouldPropagate) {
            e.stopPropagation();
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [draggedItem, getDropValidation, dropZonePath, parentId, isAdvancedSectionTarget, dropIndicator],
    );

    const handleDragLeave = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        const dzNode = e.currentTarget as HTMLElement;
        // Check if leaving to a child element within this dropzone; if so, do not clear.
        if (e.relatedTarget instanceof Node && dzNode.contains(e.relatedTarget)) {
          return;
        }

        // If the global drop indicator was specifically for THIS zone, clear it.
        if (dropIndicator && isEqual(dropIndicator.path, dropZonePath) && dropIndicator.parentId === parentId) {
          setDropIndicator(undefined);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [dropIndicator, dropZonePath, parentId],
    );

    const handleDrop = useCallback(
      (e: React.DragEvent) => {
        e.preventDefault();
        const validation = getDropValidation(draggedItem, dropZonePath, isAdvancedSectionTarget);

        if (validation.canDrop && draggedItem) {
          e.stopPropagation();
          onDropItem({ path: dropZonePath, parentId, isAdvancedSectionTarget });
        } else {
          if (!validation.shouldPropagate) {
            e.stopPropagation();
          }
          if (!validation.shouldPropagate && draggedItem) {
            endDrag();
          }
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [draggedItem, getDropValidation, dropZonePath, parentId, isAdvancedSectionTarget, onDropItem],
    );

    if (disabled) {
      return null;
    }
    const isActiveDropZone =
      dropIndicator?.show && isEqual(dropIndicator.path, dropZonePath) && dropIndicator.parentId === parentId;

    return (
      <Box
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`droppable-zone hide-in-drag-preview ${isActiveDropZone ? 'active' : ''} ${className}`}
        sx={{
          transition: 'border-color 0.2s, background-color 0.2s',
          paddingY: withDivider ? 4 : 2,
          width: '-webkit-fill-available',
          ...sx,
        }}
      >
        {hideDropZoneMessage && isActiveDropZone && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 10,
              backgroundColor: 'blue.100',
              border: '2px dashed',
              borderColor: 'blue.300',
              borderRadius: 'md',
            }}
          />
        )}
        {children}
        {withDivider && !isActiveDropZone && (
          <Flex align="center" justify="center" width="100%">
            <Box flexGrow={1} height="1px" width="100%" backgroundColor="#e6ebed" />
            <Box flex="0 0 80px" />
          </Flex>
        )}
        {!hideDropZoneMessage && (
          <HStack
            color="#586073"
            align="center"
            justify="center"
            flexGrow={1}
            sx={{
              height: dropZonePath[0] === 0 ? 200 : 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isActiveDropZone ? '#dcebf9' : '#F3F6F9',
              border: '2px dashed',
              borderColor: '#A8B1E3',
              borderRadius: theme.radii.md,
              padding: theme.space[8],
            }}
          >
            <IoMoveSharp size={24} />
            <Text fontSize="sm" fontWeight="bold">
              {t('formBuilder.dragBoxInfo')}
            </Text>
          </HStack>
        )}
      </Box>
    );
  },
);
